import React, { useState, useRef, useEffect } from "react";
import { X, Send, Maximize2 } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import KibaAvatar from "@/components/KibaAvatar";
import Linkify from "@/components/Linkify";
import { API_BASE } from "@/lib/api";
import { loadSession, saveSession, clearSession, GREETING, type ChatMsg } from "@/lib/chatSession";

const MAX_SESSION_MESSAGES = 15;

const LIMIT_REPLY =
  "You've reached the session limit for this chat. To continue, please email us at info@kibalabs.in — our team will get back to you quickly!";

function TypingDots() {
  return (
    <div className="flex items-center gap-2 px-4 py-3 bg-white/[0.06] border border-white/[0.07]">
      <div className="flex gap-1 items-center">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="w-2 h-2 rounded-full bg-[#FFC93A]"
            style={{
              animation: "kiba-bounce 1.2s ease-in-out infinite",
              animationDelay: `${i * 0.2}s`,
            }}
          />
        ))}
      </div>
      <span className="text-[11px] text-white/35 italic">Kiba is thinking…</span>
    </div>
  );
}

function StreamingCursor() {
  return (
    <span
      className="inline-block w-[2px] h-[13px] bg-[#FFC93A] ml-0.5 align-middle"
      style={{ animation: "kiba-blink 0.9s step-end infinite" }}
    />
  );
}

export default function ChatWidget() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const init = loadSession();
  const [open, setOpen]                   = useState(false);
  const [messages, setMessages]           = useState<ChatMsg[]>(init.messages);
  const [input, setInput]                 = useState("");
  const [sending, setSending]             = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const [userMsgCount, setUserMsgCount]   = useState(init.count);

  const messagesRef = useRef<HTMLDivElement>(null);

  const limitReached = userMsgCount >= MAX_SESSION_MESSAGES;
  const isStreaming  = sending && streamingText.length > 0;
  const isWaiting    = sending && streamingText.length === 0;

  // Scroll within the widget panel only — never scrolls the page
  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages, streamingText, sending]);

  const reset = () => {
    clearSession();
    setMessages([GREETING]);
    setInput("");
    setSending(false);
    setStreamingText("");
    setUserMsgCount(0);
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || sending || limitReached) return;

    const newCount = userMsgCount + 1;
    setUserMsgCount(newCount);

    const updatedMessages: ChatMsg[] = [...messages, { from: "user", text }];
    setMessages(updatedMessages);
    saveSession(updatedMessages, newCount);
    setInput("");
    setSending(true);
    setStreamingText("");

    if (newCount >= MAX_SESSION_MESSAGES) {
      setTimeout(() => {
        const final = [...updatedMessages, { from: "bot" as const, text: LIMIT_REPLY }];
        setMessages(final);
        saveSession(final, newCount);
        setSending(false);
      }, 400);
      return;
    }

    const history = updatedMessages
      .slice(1)
      .map((m) => ({ role: m.from === "user" ? "user" : "assistant", content: m.text }));

    try {
      const response = await fetch(`${API_BASE}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: history }),
      });

      if (!response.ok || !response.body) throw new Error("Stream unavailable");

      const reader  = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulated = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const lines = decoder.decode(value, { stream: true }).split("\n");
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const data = line.slice(6).trim();
          if (data === "[DONE]") break;
          try {
            const parsed = JSON.parse(data);
            if (parsed.text) {
              accumulated += parsed.text;
              setStreamingText(accumulated);
            }
          } catch { /* malformed chunk — skip */ }
        }
      }

      const botReply = accumulated || "I couldn't get a reply — please try again or email info@kibalabs.in.";
      setMessages((m) => {
        const next = [...m, { from: "bot" as const, text: botReply }];
        saveSession(next, newCount);
        return next;
      });
    } catch {
      setMessages((m) => {
        const next = [...m, { from: "bot" as const, text: "Something went wrong. Reach us at info@kibalabs.in." }];
        saveSession(next, newCount);
        return next;
      });
    } finally {
      setStreamingText("");
      setSending(false);
    }
  };

  // The full-page chat already is the assistant, so no floating button there
  if (pathname === "/talk-to-us") return null;

  return (
    <>
      <style>{`
        @keyframes kiba-bounce {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
          30% { transform: translateY(-6px); opacity: 1; }
        }
        @keyframes kiba-blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
      `}</style>

      {/* Floating toggle button */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Close chat" : "Chat with Kiba"}
        className="fixed bottom-6 right-6 z-[9999] w-16 h-16 rounded-full bg-[#FFC93A] text-[#0A1628] shadow-lg hover:scale-105 transition-transform flex items-center justify-center overflow-hidden"
        style={{ boxShadow: "0 4px 24px rgba(255,201,58,0.45)" }}
      >
        {open
          ? <X className="w-5 h-5" strokeWidth={2.5} />
          : <KibaAvatar size={56} ring={false} />
        }
      </button>

      {/* Chat window */}
      {open && (
        <div
          className="fixed bottom-24 right-6 z-[9998] w-[340px] sm:w-[380px] flex flex-col bg-[#0A1628] border border-white/10 shadow-2xl overflow-hidden"
          style={{ maxHeight: "520px", boxShadow: "0 8px 40px rgba(0,0,0,0.5)" }}
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-5 py-4 border-b border-white/[0.08] bg-[#060d1a] flex-shrink-0">
            <KibaAvatar size={38} />
            <div>
              <div className="font-display font-bold text-sm text-white">
                Kiba <span className="text-white/40 font-normal text-xs">by KIBA Labs</span>
              </div>
              <div className="flex items-center gap-1.5 mt-0.5">
                <div className="w-1.5 h-1.5 rounded-full bg-[#34D399]" />
                <span className="text-[10px] text-white/40">
                  {sending ? "Responding…" : "Online"}
                </span>
              </div>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <button
                onClick={() => { setOpen(false); navigate("/talk-to-us"); }}
                className="text-white/30 hover:text-[#FFC93A] transition-colors"
                aria-label="Open full chat"
                title="Open full chat"
              >
                <Maximize2 className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => setOpen(false)}
                className="text-white/30 hover:text-white/70 transition-colors"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div
            ref={messagesRef}
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3 min-h-0"
            style={{ maxHeight: "320px" }}
          >
            {messages.map((msg, i) => (
              <div key={i} className={`flex gap-2 ${msg.from === "user" ? "justify-end" : "justify-start"}`}>
                {msg.from === "bot" && <KibaAvatar size={26} ring={false} className="mt-0.5" />}
                <div
                  className={`max-w-[80%] px-4 py-2.5 text-[13px] leading-relaxed whitespace-pre-wrap break-words ${
                    msg.from === "user"
                      ? "bg-[#FFC93A] text-[#0A1628] font-medium"
                      : "bg-white/[0.06] text-white/85 border border-white/[0.07]"
                  }`}
                >
                  <Linkify text={msg.text} linkClass={msg.from === "user" ? "underline" : undefined} />
                </div>
              </div>
            ))}

            {isWaiting && (
              <div className="flex gap-2 justify-start">
                <KibaAvatar size={26} ring={false} className="mt-0.5" />
                <TypingDots />
              </div>
            )}

            {isStreaming && (
              <div className="flex gap-2 justify-start">
                <KibaAvatar size={26} ring={false} className="mt-0.5" />
                <div className="max-w-[80%] px-4 py-2.5 text-[13px] leading-relaxed whitespace-pre-wrap break-words bg-white/[0.06] text-white/85 border border-white/[0.07]">
                  <Linkify text={streamingText} />
                  <StreamingCursor />
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-white/[0.08] flex-shrink-0">
            {limitReached ? (
              <div className="px-4 py-3 text-[12px] text-white/40 text-center">
                Session limit reached ·{" "}
                <a href="mailto:info@kibalabs.in" className="text-[#FFC93A] hover:underline">
                  info@kibalabs.in
                </a>
                {" "}or{" "}
                <button onClick={reset} className="text-[#FFC93A] hover:underline">new chat</button>
              </div>
            ) : (
              <form onSubmit={handleSend} className="flex gap-2 p-3">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Type your question..."
                  className="flex-1 bg-white/[0.05] border border-white/10 text-white text-[13px] px-3 py-2.5 placeholder-white/25 focus:outline-none focus:border-[#FFC93A]/50"
                  disabled={sending}
                  autoFocus
                />
                <button
                  type="submit"
                  disabled={sending || !input.trim()}
                  className="w-10 h-10 flex items-center justify-center bg-[#FFC93A] text-[#0A1628] hover:bg-white transition-colors disabled:opacity-50 shrink-0"
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
            )}
            <div className="text-center pb-2 text-[9px] text-white/20 uppercase tracking-wider">
              Kiba · Powered by KIBA Labs
            </div>
          </div>
        </div>
      )}
    </>
  );
}
